/**
 * Roles and permissions.
 *
 * A capability list rather than a role check scattered through the UI. Code asks
 * `can(role, "payment.record")`, never `role === "admin"` — otherwise adding a role later means
 * finding every comparison, and one missed check is a data leak.
 *
 * Portfolio scoping (an agent seeing only their own properties) is enforced separately, in the
 * query layer, because it is a data filter rather than a permission. Both must hold.
 */

export type Role = "owner" | "admin" | "manager" | "agent" | "accountant" | "viewer";

export interface RoleInfo {
  value: Role;
  label: string;
  description: string;
  /** Roles this role is allowed to grant. Nobody may grant above themselves. */
  canAssign: Role[];
}

export const ROLES: RoleInfo[] = [
  {
    value: "owner",
    label: "Owner",
    description: "Full control, including billing and deleting the account. There is always exactly one.",
    canAssign: ["admin", "manager", "agent", "accountant", "viewer"],
  },
  {
    value: "admin",
    label: "Administrator",
    description: "Everything except billing and transferring ownership.",
    canAssign: ["manager", "agent", "accountant", "viewer"],
  },
  {
    value: "manager",
    label: "Manager",
    description: "Runs the portfolio: properties, tenancies, invoices, payments and reminders.",
    canAssign: ["agent", "viewer"],
  },
  {
    value: "agent",
    label: "Agent",
    description: "Works their own properties. Can record payments and send reminders, not change settings.",
    canAssign: [],
  },
  {
    value: "accountant",
    label: "Accountant",
    description: "Sees and reconciles all money. Cannot change tenancies or properties.",
    canAssign: [],
  },
  {
    value: "viewer",
    label: "Viewer",
    description: "Read-only across everything they are given access to.",
    canAssign: [],
  },
];

export type Permission =
  | "org.settings"
  | "org.billing"
  | "org.members"
  | "org.delete"
  | "property.view"
  | "property.edit"
  | "property.delete"
  | "party.view"
  | "party.edit"
  | "lease.view"
  | "lease.edit"
  | "lease.terminate"
  | "invoice.view"
  | "invoice.edit"
  | "invoice.void"
  | "payment.view"
  | "payment.record"
  | "payment.delete"
  | "expense.view"
  | "expense.edit"
  | "payout.view"
  | "payout.record"
  | "reminder.send"
  | "reminder.configure"
  | "maintenance.view"
  | "maintenance.edit"
  | "document.view"
  | "document.edit"
  | "report.view"
  | "audit.view"
  | "api.manage";

const ALL: Permission[] = [
  "org.settings", "org.billing", "org.members", "org.delete",
  "property.view", "property.edit", "property.delete",
  "party.view", "party.edit",
  "lease.view", "lease.edit", "lease.terminate",
  "invoice.view", "invoice.edit", "invoice.void",
  "payment.view", "payment.record", "payment.delete",
  "expense.view", "expense.edit",
  "payout.view", "payout.record",
  "reminder.send", "reminder.configure",
  "maintenance.view", "maintenance.edit",
  "document.view", "document.edit",
  "report.view", "audit.view", "api.manage",
];

const VIEW_ONLY: Permission[] = ALL.filter((p) => p.endsWith(".view"));

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  owner: ALL,
  admin: ALL.filter((p) => p !== "org.billing" && p !== "org.delete"),
  manager: [
    ...VIEW_ONLY,
    "property.edit",
    "party.edit",
    "lease.edit",
    "lease.terminate",
    "invoice.edit",
    "invoice.void",
    "payment.record",
    "expense.edit",
    "payout.record",
    "reminder.send",
    "reminder.configure",
    "maintenance.edit",
    "document.edit",
  ],
  agent: [
    "property.view",
    "party.view",
    "party.edit",
    "lease.view",
    "invoice.view",
    "payment.view",
    "payment.record",
    "reminder.send",
    "maintenance.view",
    "maintenance.edit",
    "document.view",
    "report.view",
  ],
  accountant: [
    ...VIEW_ONLY,
    "invoice.edit",
    "invoice.void",
    "payment.record",
    "payment.delete",
    "expense.edit",
    "payout.record",
  ],
  viewer: VIEW_ONLY,
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export function canAny(role: Role, permissions: Permission[]): boolean {
  return permissions.some((p) => can(role, p));
}

export function canAll(role: Role, permissions: Permission[]): boolean {
  return permissions.every((p) => can(role, p));
}

export function roleInfo(role: Role): RoleInfo {
  return ROLES.find((r) => r.value === role) ?? ROLES[ROLES.length - 1];
}

export function canAssignRole(actor: Role, target: Role): boolean {
  return roleInfo(actor).canAssign.includes(target);
}

/** True when this role only ever sees the properties explicitly assigned to it. */
export function isPortfolioScoped(role: Role): boolean {
  return role === "agent";
}

/** Thrown by the server-action wrapper; caught and turned into a 403 rather than a stack trace. */
export class PermissionError extends Error {
  readonly permission: Permission;
  constructor(permission: Permission) {
    super(`You do not have permission to do that (${permission}).`);
    this.name = "PermissionError";
    this.permission = permission;
  }
}

export function assertCan(role: Role, permission: Permission): void {
  if (!can(role, permission)) throw new PermissionError(permission);
}
